// src/hooks/useUserRole.js
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";

export function normalizeRole(role) {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");
}

export function isSuperRole(role) {
  const r = normalizeRole(role);
 return r === "super user";
}

export function isAdminRole(role) {
  const r = normalizeRole(role);
  return r === "admin" || isSuperRole(role);
}

export function isManagerRole(role) {
  return normalizeRole(role) === "manager";
}

export function isSupervisorRole(role) {
  const r = normalizeRole(role);
  return r === "supervisor" || isAdminRole(role);
}

/**
 * useUserRole()
 * - Reads role from custom claims if present
 * - Falls back to users/{uid}.role
 * - No false timeouts after success
 */
const ROLE_LOAD_TIMEOUT_MS = 15000;

export default function useUserRole() {
  const { user, loading: authLoading } = useAuth();

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("init");

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const finish = (next) => {
      if (cancelled) return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;

      if (next?.role !== undefined) setRole(next.role);
      if (next?.source) setSource(next.source);
      setLoading(false);
    };

    setLoading(true);
    setSource("init");

    timeoutId = setTimeout(() => {
      if (cancelled) return;
      console.error(
        `[useUserRole] timeout after ${ROLE_LOAD_TIMEOUT_MS}ms (authLoading=${authLoading}, user=${!!user})`
      );
      setSource("timeout");
      setLoading(false);
    }, ROLE_LOAD_TIMEOUT_MS);

    async function run() {
      if (authLoading) return;

      if (!user) {
        finish({ role: null, source: "none" });
        return;
      }

      try {
        const tokenResult = await user.getIdTokenResult();
        const claimRole = tokenResult?.claims?.role || null;

        if (claimRole) {
          console.log("[useUserRole] role from claims:", claimRole);
          finish({ role: claimRole, source: "claims" });
          return;
        }

        const snap = await getDoc(doc(db, "users", user.uid));
        const firestoreRole = snap.exists() ? snap.data()?.role || null : null;

        console.log("[useUserRole] role from firestore:", firestoreRole);
        finish({ role: firestoreRole, source: "firestore" });
      } catch (err) {
        console.error("[useUserRole] failed:", err);
        finish({ source: "error" });
      }
    }

    run();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, authLoading]);

  return { role, loading, source };
}